import { NavLink, Outlet, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, Image, Film, Heart, MessageSquare, Instagram, Inbox, Settings, LayoutDashboard, ExternalLink, Images, HardDrive } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/weddings", label: "Weddings", icon: Heart },
  { to: "/admin/films", label: "Films", icon: Film },
  { to: "/admin/pre-wedding", label: "Pre-Wedding", icon: Image },
  { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquare },
  { to: "/admin/instagram", label: "Instagram", icon: Instagram },
  { to: "/admin/inquiries", label: "Inquiries", icon: Inbox },
  { to: "/admin/client-galleries", label: "Client Galleries", icon: Images },
  { to: "/admin/gallery", label: "Media / Drive", icon: HardDrive },
  { to: "/admin/settings", label: "Website Settings", icon: Settings },
];

export default function AdminLayout() {
  const { session, user, isAdmin, loading, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  if (loading) {
    return <div className="min-h-screen bg-[#070707] flex items-center justify-center text-[#C99A45]">Checking admin session...</div>;
  }
  if (!session || !user || !isAdmin) {
    return <Navigate to="/admin/login" replace state={{ from: location }} />;
  }

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div className="admin-shell admin-layout flex" data-testid="admin-layout">
      <aside className="admin-sidebar w-72 flex flex-col">
        <div className="p-6 border-b border-[#C99A45]/15">
          <div className="admin-sidebar-brand font-serif-display text-2xl">Mangalfera Films</div>
          <div className="admin-sidebar-kicker eyebrow text-[9px] mt-1">Professional CMS</div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `admin-sidebar-link ${isActive ? "admin-sidebar-link-active" : "admin-sidebar-link-inactive"}`
              }
            >
              <Icon size={15} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#C99A45]/15 space-y-2">
          <a href="/" target="_blank" rel="noreferrer" className="admin-sidebar-footer-link" data-testid="view-site-link">
            <ExternalLink size={12} /> View Site
          </a>
          <button onClick={doLogout} className="admin-sidebar-footer-link admin-sidebar-logout" data-testid="admin-logout">
            <LogOut size={12} /> Logout
          </button>
          <div className="admin-sidebar-email" title={user.email}>{user.email}</div>
        </div>
      </aside>
      <main className="admin-content flex-1 overflow-auto min-w-0">
        <div className="admin-topbar px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-[#171717]">Admin Console</div>
            <div className="text-xs text-[#6f6a62]">Manage public content, media, and private client galleries.</div>
          </div>
          <div className="hidden sm:flex items-center gap-3">
            <div className="text-right">
              <div className="text-xs font-medium text-[#171717]">Signed in</div>
              <div className="text-[11px] text-[#6f6a62] max-w-[220px] truncate">{user.email}</div>
            </div>
            <button onClick={doLogout} className="btn-gold-outline !py-2 !px-4" data-testid="admin-topbar-logout">
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
